import { Button, Layout, message, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import ActionHeader from '../../../components/actionHeader';
import Page from '../../../components/page';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store';
import { StyledContent, StyledMenu } from './styles';
import {
  fetchLinkedMachines,
  fetchTemplateById,
  resetTemplate,
  selectDraftTemplate,
  selectIsFetchingTemplates,
} from '../../../slices/templates';
import { fetchMachines } from '../../../slices/machines';
import { copyTemplate, deleteTemplates } from '../../../utils/httpRequest';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { resetSolutionGrids } from '../../../slices/solutionGrids';
import { useTranslation } from 'react-i18next';

const TemplateDetailsContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id = '' } = useParams();
  const theme = useTheme();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();

  const template = useSelector(selectDraftTemplate);
  const isFetching = useSelector(selectIsFetchingTemplates);

  const { pathname } = location;

  const selectedKeys = pathname.split('/').slice(4);

  const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const menus = [
    { key: '', label: t('details') },
    { key: 'solutionGrid', label: t('solution_grid') },
    { key: 'issue', label: t('issue') },
    { key: 'cause', label: t('cause') },
    { key: 'routine', label: t('routine') },
    { key: 'resourceLibrary', label: t('resource_library') },
  ];

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);
      setLoading(true);
      await deleteTemplates(id);
      navigate('/knowledge/template');
    } catch (err: any) {
      messageApi.error(err?.message || t('delete_failed_capitalized'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      setLoading(true);
      const res = await copyTemplate(id);
      modal.confirm({
        title: t('template_copied'),
        icon: (
          <CheckCircleOutlined style={{ color: theme.antd.colorPrimary }} />
        ),
        content: t('go_to_copied_template'),
        okText: t('confirm'),
        onOk() {
          navigate(`/knowledge/template/${res.templateID}`);
        },
        cancelText: t('stay_here'),
      });
    } catch (err: any) {
      console.log(err);
      messageApi.error(err?.message || t('copy_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetTemplate());
    dispatch(resetSolutionGrids());
    dispatch(fetchTemplateById(id));
    dispatch(fetchLinkedMachines(id));
    dispatch(fetchMachines());
  }, [id]);

  return (
    <Page
      backgroundColor="#f6f6f6"
      gap={0}
      header={
        <ActionHeader
          title={template?.name}
          showBack
          onBack={() => navigate(-1)}
          endSlot={
            <>
              <Button
                type="primary"
                danger
                ghost
                onClick={() => setShowDeleteModal(true)}
              >
                {t('delete')}
              </Button>
              <Button type="primary" ghost onClick={handleCopy}>
                {t('copy')}
              </Button>
            </>
          }
        />
      }
    >
      {contextHolder}
      {modalContextHolder}
      <Spin spinning={isFetching} fullscreen />
      <Layout style={{ height: '100%' }}>
        <Layout.Sider style={{ backgroundColor: '#f7f7f7' }} width={144}>
          <StyledMenu
            selectedKeys={selectedKeys[0] ? selectedKeys : ['']}
            defaultOpenKeys={defaultOpenKeys}
            theme="light"
            items={menus}
            onClick={({ key, keyPath }) => {
              navigate(key, { replace: true });
              setDefaultOpenKeys(keyPath);
            }}
          />
        </Layout.Sider>
        <StyledContent>
          <Outlet />
        </StyledContent>
        <Modal
          title={t('delete_template', { name: template?.name })}
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={showDeleteModal}
          onOk={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        >
          <p>{t('confirm_delete_template', { name: template?.name })}</p>
        </Modal>
        <Spin fullscreen spinning={loading}>
          {t('deleting')}
        </Spin>
      </Layout>
    </Page>
  );
};

export default TemplateDetailsContainer;
